import { INVASION, OUTPOSTS, SHIP_CLASSES, SHIP_COMBAT } from '@sd/content';
import {
  atWar,
  capitalHex,
  garrisonStrength,
  hasAgreement,
  hexDistance,
  isExplored,
  planetGuns,
  techMods,
  type Command,
  type GameState,
  type Hex,
  type PlayerState,
} from '@sd/sim';
import {
  armedShips,
  at,
  canBuy,
  caravanFleets,
  gsCash,
  home,
  mergeAt,
  orderShip,
  sendTo,
  strength,
  threatened,
  warFleets,
  warOn,
  warship,
  wellFed,
} from './fleets';
import type { Bot } from './match';
import { frontier } from './scout';

/**
 * Phase 2 military bots (GDD §8, §10): a warlord that goes for domination,
 * a guard every capital keeps when war comes, and the response to a rival's
 * victory countdown. Bot strategy, not game rules: thresholds live here.
 */

/** Until this tick a warlord in a big lobby settles a Lagrange point before arming (20 min). */
const LAGRANGE_DEADLINE = 12_000;

/** ГС a bot keeps when buying warships. */
const CUSHION = 300;

const me = (state: GameState, id: string) => state.players.find((p) => p.id === id);

/** The rival a warlord goes after: the weakest free capital, nearest first on ties. */
export function prey(state: GameState, self: PlayerState): PlayerState | null {
  const from = capitalHex(state, self.id);
  // Only a rival whose capital it has found (#100), or one counting down to a victory:
  // that the whole system hears of.
  const counting = (id: string) => (state.countdowns.some((c) => c.playerId === id) ? 1 : 0);
  const others = state.players.filter(
    (p) =>
      p.id !== self.id &&
      p.vassalOf !== self.id &&
      // Never an ally, a teammate above all (#137): war on them is refused.
      !hasAgreement(state.agreements, self.id, p.id, 'alliance') &&
      (counting(p.id) === 1 || isExplored(state, self.id, capitalHex(state, p.id))),
  );
  // With three or more players a vassal's Lagrange stations count towards domination:
  // the rival holding most of them comes first.
  const stations = (id: string) =>
    state.players.length >= 3
      ? state.outposts.filter((o) => o.owner === id && o.kind === 'lagrangeStation').length
      : 0;
  // Whoever counts down to a victory comes first: a vassal cannot win.
  others.sort(
    (a, b) =>
      counting(b.id) - counting(a.id) ||
      stations(b.id) - stations(a.id) ||
      strength(state, a.id) +
        garrisonStrength(a.planet) -
        (strength(state, b.id) + garrisonStrength(b.planet)) ||
      hexDistance(from, capitalHex(state, a.id)) - hexDistance(from, capitalHex(state, b.id)) ||
      a.id.localeCompare(b.id),
  );
  return others[0] ?? null;
}

/** Landers a landing needs against `target`'s garrison (#135). */
const landersFor = (target: PlayerState) =>
  Math.floor(garrisonStrength(target.planet) / INVASION.troopsPerLander) + 1;

export interface WarlordStyle {
  /** Warships before the first declaration of war. */
  readonly warships: number;
}

/**
 * Domination (GDD §10): builds a war fleet and landers, declares war on
 * the weakest rival, flies to its capital, lands when it can (orbital
 * assault) and holds the siege; raids the capital or its outposts otherwise.
 */
/** Hull share at which a warlord's fleets turn home (#117). */
const WARLORD_RETREAT = 0.25;

export function makeWarlord(style: WarlordStyle): Bot {
  return (state, playerId, rng) => {
    if (state.pause || !rng.chance(0.05)) return [];
    const self = me(state, playerId);
    if (!self || self.vassalOf !== null) return [];
    // While its domination counts down, the fleet sits over a vassal's capital so it cannot rise.
    const vassal = state.players.find((p) => p.vassalOf === playerId);
    if (
      vassal &&
      state.countdowns.some((c) => c.playerId === playerId && c.kind === 'domination')
    ) {
      const main = warFleets(state, playerId).sort((a, b) => b.ships.length - a.ships.length)[0];
      const hold = main && sendTo(playerId, main, capitalHex(state, vassal.id));
      return hold ? [hold] : [];
    }
    const target = prey(state, self);
    if (!target) {
      // No rival found yet (#100): a ready war fleet goes looking.
      const main = warFleets(state, playerId).sort((a, b) => b.ships.length - a.ships.length)[0];
      if (!main || main.path.length > 0 || armedShips(state, playerId) < style.warships) return [];
      const unknown = frontier(state, playerId, main.hex);
      return unknown ? [{ type: 'moveFleet', playerId, fleetId: main.id, to: unknown }] : [];
    }
    if (!wellFed(self)) return [];
    const base = home(state, playerId);
    const ships = armedShips(state, playerId);
    const canLand = self.techs.includes(INVASION.requiresTech);
    const landers = state.fleets
      .filter((f) => f.owner === playerId && !caravanFleets(state).has(f.id))
      .reduce((n, f) => n + f.ships.filter((s) => s.cls === 'lander').length, 0);

    // 0. With three or more players, domination needs Lagrange stations: settle one first
    // while points are free, and leave the treasury to the settler meanwhile.
    if (state.players.length >= 3 && !atWar(state, playerId, target.id)) {
      const mine = state.outposts.some((o) => o.owner === playerId && o.kind === 'lagrangeStation');
      const taken = new Set(state.outposts.map((o) => o.id));
      const free = state.system.lagrangePoints.some((l) => !taken.has(l.id));
      if (!mine && free && state.tick < LAGRANGE_DEADLINE) return [];
    }

    // 1. Build up: warships, then landers once landings are possible; buy the alloys they take.
    const atWarNow = atWar(state, playerId, target.id);
    // War comes first once the fleet is ready; landers follow while it starts.
    if (ships >= style.warships && !warOn(state, playerId, target.id)) {
      return [{ type: 'declareWar', playerId, target: target.id }];
    }
    const want =
      ships < style.warships
        ? warship(self, ships)
        : canLand && landers < landersFor(target)
          ? 'lander'
          : atWarNow && ships < Math.max(style.warships, armedShips(state, target.id) + 2)
            ? warship(self, ships)
            : null;
    if (want) {
      const order = orderShip(self, want, CUSHION);
      if (order) return [order];
      const alloys = Math.ceil(SHIP_CLASSES[want].cost.alloys - self.goods.alloys);
      const price = alloys * state.market.alloys.price * 1.3;
      if (
        alloys > 0 &&
        canBuy(state, playerId, 'alloys') &&
        gsCash(self) > SHIP_CLASSES[want].cost.cash + price + CUSHION
      ) {
        return [{ type: 'marketBuy', playerId, good: 'alloys', quantity: Math.min(200, alloys) }];
      }
    }

    // 2. Wait for the war to start.
    if (!atWarNow) return [];
    // Under fire from afar (#117) the default retreat at half hull turned every attack
    // back before it arrived: the war fleets press on to a quarter.
    const wavering = warFleets(state, playerId).find(
      (f) => (f.retreatAt ?? 0.5) > WARLORD_RETREAT && !f.retreating,
    );
    if (wavering) {
      return [
        {
          type: 'setStance',
          playerId,
          fleetId: wavering.id,
          stance: 'aggressive',
          retreatAt: WARLORD_RETREAT,
        },
      ];
    }

    // 3. Gather at home, then strike as one fleet.
    const siege = state.sieges.find((s) => s.owner === playerId && s.victim === target.id);
    const enemyHome = capitalHex(state, target.id);
    const fleets = warFleets(state, playerId);
    const main = fleets.sort((a, b) => b.ships.length - a.ships.length)[0];
    if (!main) return [];
    if (siege) {
      // Hold the siege: the fleet stays over the capital until it falls.
      const back = main.path.length === 0 ? sendTo(playerId, main, enemyHome) : null;
      return back ? [back] : [];
    }
    if (at(main, base)) {
      const merge = mergeAt(state, playerId, base);
      if (merge) return [merge];
      // Strike once the defenders the target raised can be beaten: two more than they have.
      // The army is every war fleet at home (#160: fleets merge only with «Логистика
      // флота», four ships at most), and it sets off together.
      const army = fleets.filter((f) => at(f, base) && f.path.length === 0);
      const theirs = armedShips(state, target.id);
      const urgent = state.countdowns.some((c) => c.playerId === target.id);
      const guns = army.reduce(
        (n, f) => n + f.ships.filter((s) => SHIP_COMBAT[s.cls].damage > 0).length,
        0,
      );
      const ready = urgent || guns >= Math.max(style.warships, theirs + 2);
      if (!ready) return [];
      const go = army.flatMap((f) => sendTo(playerId, f, enemyHome) ?? []);
      if (go.length) return go;
    }
    if (!at(main, enemyHome)) {
      // On the way, the strongest outpost nearby is fair game.
      const outpost = state.outposts.find((o) => o.owner === target.id && at(main, o.hex));
      if (outpost && main.path.length === 0) return [{ type: 'raid', playerId, fleetId: main.id }];
      const move = sendTo(playerId, main, enemyHome);
      return move ? [move] : [];
    }
    if (main.path.length > 0) return [];
    const defended = state.fleets.some(
      (f) =>
        f.owner === target.id &&
        at(f, enemyHome) &&
        f.ships.some((s) => SHIP_COMBAT[s.cls].damage > 0),
    );
    if (defended) return [];
    if (canLand) {
      // Land with whichever of our fleets over the capital carries enough troops;
      // otherwise bring the landers over.
      const troops = (f: (typeof state.fleets)[number]) =>
        f.ships.filter((x) => x.cls === 'lander').length * INVASION.troopsPerLander;
      const mine = state.fleets.filter(
        (f) => f.owner === playerId && !caravanFleets(state).has(f.id) && troops(f) > 0,
      );
      const lander = mine.find(
        (f) =>
          at(f, enemyHome) && f.path.length === 0 && troops(f) > garrisonStrength(target.planet),
      );
      if (lander) return [{ type: 'invade', playerId, fleetId: lander.id }];
      const carrier = mine.find((f) => !at(f, enemyHome) && f.path.length === 0);
      const move = carrier && sendTo(playerId, carrier, enemyHome);
      if (move) return [move];
    }
    if (state.raids.some((r) => r.fleetId === main.id)) return [];
    if (planetGuns(target.planet) < strength(state, playerId)) {
      return [{ type: 'raid', playerId, fleetId: main.id }];
    }
    // The capital is too strong: go for its richest outpost instead.
    const outposts = state.outposts
      .filter((o) => o.owner === target.id)
      .sort(
        (a, b) => OUTPOSTS[b.kind].output - OUTPOSTS[a.kind].output || a.id.localeCompare(b.id),
      );
    const next = outposts[0];
    const move = next && sendTo(playerId, main, next.hex);
    return move ? [move] : [];
  };
}

/**
 * Every capital's guard: when war is declared on it, it keeps a few
 * warships at home, and sends them back home from wherever they wander.
 */
export function makeGuard(minShips: number): Bot {
  return (state, playerId, rng) => {
    if (state.pause || !rng.chance(0.05)) return [];
    const self = me(state, playerId);
    if (!self || !threatened(state, playerId)) return [];
    if (armedShips(state, playerId) < minShips) {
      const order = orderShip(self, warship(self, armedShips(state, playerId)), CUSHION / 2);
      if (order) return [order];
    }
    return [];
  };
}

/** Where a rival's countdown can be broken: its swarm, or its capital. */
function counterTarget(state: GameState, rival: string, kind: string): Hex {
  if (kind === 'technological') {
    const m = state.megastructures.find((x) => x.owner === rival);
    if (m) return m.hex;
  }
  if (kind === 'domination') {
    const station = state.outposts.find((o) => o.owner === rival && o.kind === 'lagrangeStation');
    if (station) return station.hex;
  }
  return capitalHex(state, rival);
}

/**
 * The answer to a rival's victory countdown (GDD §10): declare war on the
 * leader and send the war fleet to break it — the swarm site, a Lagrange
 * station, or the capital. Builds a couple of warships if it has none.
 */
export const responderBot: Bot = (state, playerId, rng) => {
  if (state.pause || !rng.chance(0.05)) return [];
  const self = me(state, playerId);
  if (!self || self.vassalOf !== null) return [];
  const threat = state.countdowns.find(
    (c) =>
      c.playerId !== playerId &&
      self.vassalOf !== c.playerId &&
      !hasAgreement(state.agreements, playerId, c.playerId, 'alliance'),
  );
  if (!threat) return [];
  const rival = threat.playerId;
  if (armedShips(state, playerId) < 2) {
    const order = orderShip(self, warship(self, armedShips(state, playerId)), CUSHION / 3);
    if (order) return [order];
  }
  if (!warOn(state, playerId, rival)) return [{ type: 'declareWar', playerId, target: rival }];
  if (!atWar(state, playerId, rival)) return [];
  const goal = counterTarget(state, rival, threat.kind);
  const commands: Command[] = [];
  for (const f of warFleets(state, playerId)) {
    const move = sendTo(playerId, f, goal);
    if (move) commands.push(move);
    else if (
      threat.kind !== 'technological' &&
      f.path.length === 0 &&
      !state.raids.some((r) => r.fleetId === f.id)
    ) {
      commands.push({ type: 'raid', playerId, fleetId: f.id });
    }
  }
  return commands;
};

/** A vassal tries to break free now and then (GDD §8: the rebellion). */
export const rebelBot: Bot = (state, playerId, rng) => {
  if (state.pause || !rng.chance(0.01)) return [];
  const self = me(state, playerId);
  return self?.vassalOf ? [{ type: 'declareIndependence', playerId }] : [];
};

/** Whether a player could build the swarm now (used to keep a transport for it). */
export const canBuildSwarm = (self: PlayerState): boolean => techMods(self.techs).megastructure;
